import { useState } from 'react';

import { useDispatch } from 'react-redux';

import {
  type TechRelationship,
  useCreateTechRelationshipMutation,
  useDeleteTechRelationshipMutation,
  useGetTechRelationshipsQuery,
} from '@/app/services/techRelationshipsRest';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/**
 * Minimal admin surface for `tog2_site_tech_relationships`. Lets an
 * administrator wire an OvaDrive SystemUser (tech) to a site-scoped
 * external "herd tech" identifier so that imported rows which reference
 * the herd-management tech name can be resolved to a nucleus tech.
 *
 * Intentionally no-frills — this is not a high-traffic admin screen.
 */
export function TechRelationshipsAdminPage() {
  const dispatch = useDispatch();

  const { data, isFetching } = useGetTechRelationshipsQuery({});
  const [createRelationship, { isLoading: isCreating }] =
    useCreateTechRelationshipMutation();
  const [deleteRelationship] = useDeleteTechRelationshipMutation();

  const [siteId, setSiteId] = useState('');
  const [techId, setTechId] = useState('');
  const [herdTechId, setHerdTechId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const resetForm = () => {
    setSiteId('');
    setTechId('');
    setHerdTechId('');
    setStartDate('');
    setEndDate('');
  };

  const handleCreate = async () => {
    if (!siteId || !techId || !herdTechId) {
      dispatch(
        addToast({
          severity: 'warn',
          summary: 'Missing fields',
          detail: 'Site, tech, and herd tech ID are required.',
          life: ToastLife.SHORT,
        }),
      );
      return;
    }
    try {
      await createRelationship({
        siteId,
        techId,
        herdTechId,
        startDate: startDate || null,
        endDate: endDate || null,
      }).unwrap();
      dispatch(
        addToast({
          severity: 'success',
          summary: 'Relationship added',
          life: ToastLife.SHORT,
        }),
      );
      resetForm();
    } catch (err) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Failed to create relationship',
          detail: err instanceof Error ? err.message : String(err),
          life: ToastLife.LONG,
        }),
      );
    }
  };

  const handleDelete = async (row: TechRelationship) => {
    try {
      await deleteRelationship(row.relationshipId).unwrap();
      dispatch(
        addToast({
          severity: 'success',
          summary: 'Relationship removed',
          life: ToastLife.SHORT,
        }),
      );
    } catch (err) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Failed to delete relationship',
          detail: err instanceof Error ? err.message : String(err),
          life: ToastLife.LONG,
        }),
      );
    }
  };

  const rows = data ?? [];

  return (
    <div className="flex flex-col gap-4 p-4">
      <header>
        <h1 className="text-lg font-semibold sm:text-2xl">
          Tech relationships
        </h1>
        <p className="text-muted-foreground text-sm">
          Link an OvaDrive tech to a site-scoped herd-management tech ID. Used
          by fresh-straw imports to resolve external tech names back to a
          nucleus technician.
        </p>
      </header>

      <section className="ring-border bg-background flex flex-col gap-3 rounded-lg p-4 ring-1">
        <h2 className="text-sm font-semibold">Add relationship</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tech-rel-site">Site ID</Label>
            <Input
              id="tech-rel-site"
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
              placeholder="GUID"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tech-rel-tech">Tech (SystemUser) ID</Label>
            <Input
              id="tech-rel-tech"
              value={techId}
              onChange={(e) => setTechId(e.target.value)}
              placeholder="GUID"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tech-rel-herd">Herd tech ID</Label>
            <Input
              id="tech-rel-herd"
              value={herdTechId}
              onChange={(e) => setHerdTechId(e.target.value)}
              placeholder="external identifier"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tech-rel-start">Start date</Label>
            <Input
              id="tech-rel-start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tech-rel-end">End date</Label>
            <Input
              id="tech-rel-end"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="flex items-end">
            <Button
              type="button"
              onClick={handleCreate}
              disabled={isCreating}
              className="w-full"
            >
              {isCreating ? 'Saving…' : 'Add'}
            </Button>
          </div>
        </div>
      </section>

      <section className="ring-border bg-background flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg ring-1">
        <div className="border-b px-4 py-2">
          <h2 className="text-sm font-semibold">
            Existing relationships{' '}
            <span className="text-muted-foreground text-xs">
              ({rows.length})
            </span>
          </h2>
        </div>
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase">
                  Site
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase">
                  Tech
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase">
                  Herd ID
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase">
                  Start
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase">
                  End
                </th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {isFetching && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="text-muted-foreground px-3 py-4 text-center"
                  >
                    Loading…
                  </td>
                </tr>
              )}
              {!isFetching && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="text-muted-foreground px-3 py-4 text-center"
                  >
                    No relationships configured.
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <tr key={row.relationshipId} className="border-t">
                  <td className="px-3 py-1.5">{row.siteName ?? row.siteId}</td>
                  <td className="px-3 py-1.5">{row.techName ?? row.techId}</td>
                  <td className="px-3 py-1.5 font-mono text-xs">
                    {row.herdTechId}
                  </td>
                  <td className="px-3 py-1.5 text-xs tabular-nums">
                    {row.startDate ? row.startDate.slice(0, 10) : '—'}
                  </td>
                  <td className="px-3 py-1.5 text-xs tabular-nums">
                    {row.endDate ? row.endDate.slice(0, 10) : '—'}
                  </td>
                  <td className="px-3 py-1.5">
                    <Button
                      type="button"
                      size="xs"
                      variant="ghost"
                      onClick={() => handleDelete(row)}
                      aria-label={`Delete relationship ${row.relationshipId}`}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
